import { DeliveryMethod, DeliveryPreferenceType, useBill, useDeliveryMethods, usePayments } from '@melio/platform-api';
import { convertDateToStringRepresentation, useBoolean, useDateUtils } from '@melio/platform-utils';
import { useCallback, useEffect, useMemo } from 'react';
import { atom, useRecoilState } from 'recoil';

import { PaymentDraft } from '../types';

export type PaymentBuilder = {
  setFundingSourceId: (fundingSourceId: string) => Promise<void>;
  setScheduledDate: (scheduledDate: Date) => Promise<void>;
  setNoteToSeller: (note: string) => Promise<void>;
  createPayment: (data?: PaymentDraft) => Promise<string>;
  update: (data: PaymentDraft) => Promise<void>;
  data: PaymentDraft;
  isUpdating: boolean;
  isCreating: boolean;
};

const paymentDraftAtom = atom<PaymentDraft>({
  key: 'paymentDraft',
  default: {},
});

type UsePaymentBuilderProps = {
  billId?: string;
};

export const getDeliveryPreferenceType = (deliveryMethod?: DeliveryMethod): DeliveryPreferenceType | undefined => {
  switch (deliveryMethod?.type) {
    case 'bank-account':
      return 'ach';
    case 'paper-check':
      return 'check';
    case 'card':
      return 'card';
    case 'virtual-card':
      return 'virtual-card';
    default:
      return undefined;
  }
};

export const usePaymentBuilder = ({ billId }: UsePaymentBuilderProps): PaymentBuilder => {
  const [paymentDraft, setPaymentDraft] = useRecoilState(paymentDraftAtom);
  const [isUpdating, updatingState] = useBoolean(false);
  const { createDate } = useDateUtils();
  const { data: bill } = useBill({ id: billId });
  const { data: deliveryMethods } = useDeliveryMethods({ vendorId: bill?.vendorId });
  const { create: _createPayment, isMutating: isCreating } = usePayments({ enabled: false });

  useEffect(() => {
    if (bill) {
      const deliveryMethod = deliveryMethods?.[0];
      const deliveryPreferenceType = getDeliveryPreferenceType(deliveryMethod);
      setPaymentDraft((prev) => ({
        ...prev,
        billId: bill.id,
        deliveryMethodId: deliveryMethod?.id,
        scheduledDate: createDate(),
        deliveryPreferenceType,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill, deliveryMethods]);

  const isValidPaymentDraft = useCallback(
    (draft: PaymentDraft): draft is Required<PaymentDraft> => {
      const { deliveryMethodId, fundingSourceId, scheduledDate } = draft;
      const { id: billId, amount: paymentAmount, vendorId } = bill || {};
      if (billId && scheduledDate && fundingSourceId && deliveryMethodId && paymentAmount && vendorId) {
        return true;
      }
      return false;
    },
    [bill]
  );

  const createPayment = async (data: PaymentDraft = {}) => {
    const target = { ...paymentDraft, ...data };

    const deliveryMethod = deliveryMethods?.[0];
    const deliveryPreference = getDeliveryPreferenceType(deliveryMethod);

    if (deliveryMethod && deliveryPreference && bill && isValidPaymentDraft(target)) {
      const payment = await _createPayment({
        deliveryMethodId: target.deliveryMethodId,
        fundingSourceId: target.fundingSourceId,
        scheduledDate: convertDateToStringRepresentation(target.scheduledDate),
        paymentBillsInfo: [{ id: bill.id, paymentAmount: bill.amount }],
        vendorId: bill.vendorId,
        deliveryPreference,
      });
      return payment.id;
    }
    throw new Error('Missing required fields');
  };

  const update = (data: PaymentDraft): Promise<void> => {
    updatingState.on();
    setPaymentDraft((prev) => ({ ...prev, ...data }));
    return new Promise((resolve) => setTimeout(resolve, 1)).then(updatingState.off);
  };

  return {
    data: useMemo(() => paymentDraft, [JSON.stringify(paymentDraft)]), // eslint-disable-line react-hooks/exhaustive-deps
    update,
    setFundingSourceId: (fundingSourceId: string) => update({ fundingSourceId }),
    setNoteToSeller: (note: string) => update({ note }),
    setScheduledDate: (scheduledDate: Date) => update({ scheduledDate }),
    createPayment,
    isUpdating,
    isCreating,
  };
};
